import { useState } from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, Building, Briefcase, Cpu, Users, DollarSign, HelpCircle } from 'lucide-react';
import { useJsonLd } from '@/hooks/useJsonLd';

const FAQ_SECTIONS = [
  {
    id: 'firm',
    title: 'Firm & Capabilities',
    icon: Building,
    questions: [
      {
        q: "What is KGOB?",
        a: "KGOB CPAs & Advisors (Kohari Gonzalez Oneyear & Brown PLLC) is a North Carolina\u2013based CPA firm offering luxury advisory and precision compliance \u2014 tax, accounting, payroll, business advisory, and IRS resolution \u2014 for business owners, professionals, and families across the United States."
      },
      {
        q: "Where is KGOB located?",
        a: "KGOB has four offices across North Carolina: Charlotte HQ (2740 East WT Harris Blvd), Charlotte Fairview (6000 Fairview Rd, by appointment), Winston-Salem (326 S. Main St), and Mooresville (121 Rolling Hill Rd). We serve clients nationwide."
      },
      {
        q: "Who does KGOB serve best?",
        a: "We work best with established, growth-minded small businesses, high-earning professionals, business-owning families, and clients with multi-state or more complex tax situations who value a long-term advisory partnership."
      },
      {
        q: "What makes KGOB different from a typical CPA firm?",
        a: "We\u2019re a planning-focused, advisory-first firm \u2014 not a high-volume tax shop. We\u2019re selective by design, run AI-powered tools alongside expert human CPAs, and build long-term relationships rather than once-a-year filings."
      },
      {
        q: "How long has KGOB been in business?",
        a: "KGOB has provided trusted CPA services for more than 30 years and has guided over 10,000 individuals and organizations across our four NC offices."
      },
      {
        q: "Who leads KGOB?",
        a: "KGOB is led by Managing Partner Sara Gonzalez, CPA, alongside the firm\u2019s named partners \u2014 Kohari, Gonzalez, Oneyear, and Brown."
      },
    ]
  },
  {
    id: 'services',
    title: 'Services Offered',
    icon: Briefcase,
    questions: [
      {
        q: "What services does KGOB offer?",
        a: "KGOB offers tax preparation, advanced tax strategy, bookkeeping, payroll, business advisory, IRS resolution, business valuation, audit support, and AI-powered financial tools \u2014 all under one firm."
      },
      {
        q: "Does KGOB offer bookkeeping and payroll for small businesses?",
        a: "Yes. For business clients, most relationships begin with bookkeeping and payroll compliance \u2014 the foundation for tax savings, better decisions, and eventual exit or succession planning. These services are also delivered through our operational arm at carolinas.tax, our original and still-active website."
      },
      {
        q: "Does KGOB handle multi-state tax returns?",
        a: "Yes \u2014 we regularly serve clients with multi-state and more complex tax situations across the United States."
      },
      {
        q: "Does KGOB provide IRS audit defense?",
        a: "Yes \u2014 through LetsTalkRelief.com and our advisory team, we represent and support clients through IRS notices, back taxes, audits, and compliance issues with secure document tracking and expert guidance."
      },
      {
        q: "Does KGOB provide tax planning, not just tax preparation?",
        a: "Yes. Year-round, planning-focused tax strategy is core to how we work. Through LetsTalkStrategy.io we deliver advanced tax planning for high-income individuals and business owners."
      },
      {
        q: "Does KGOB offer business valuation services?",
        a: "Yes. Through LetsTalkGrowth.com, we provide data-driven valuation tools and AI-powered insights so business owners can measure their company\u2019s worth and grow its value strategically."
      },
      {
        q: "Does KGOB offer pro bono or nonprofit services?",
        a: "Yes. Through our KGOB Cares / If You Teach Me program, we provide free tax preparation and advisory work for qualifying nonprofits, plus scholarships and financial literacy workshops."
      },
      {
        q: "Can KGOB help me estimate my 2025 tax liability?",
        a: "Yes \u2014 our free LetsTalkTaxes.io tool helps individuals and business owners estimate 2025 tax liability, see the impact of new tax law changes, and plan ahead with confidence."
      },
    ]
  },
  {
    id: 'technology',
    title: 'Technology & Innovation',
    icon: Cpu,
    questions: [
      {
        q: "What is AskSara.cpa?",
        a: "AskSara.cpa is KGOB\u2019s proprietary AI advisor that decodes tax data, uncovers potential savings, and guides confident financial decisions by combining human CPA expertise with machine precision."
      },
      {
        q: "What is BizHub.com?",
        a: "BizHub is KGOB\u2019s platform for business owners \u2014 combining curated educational content, implementation tools, vetted expert services, and community collaboration to help entrepreneurs launch smarter and scale stronger."
      },
      {
        q: "What is LetsTalkGrowth.com?",
        a: "LetsTalkGrowth.com is KGOB\u2019s AI-powered business valuation and value-building platform that helps owners understand what their company is worth and how to grow it."
      },
      {
        q: "What is LetsTalkStrategy.io?",
        a: "LetsTalkStrategy.io is KGOB\u2019s advanced tax strategy platform for high-income individuals and business owners \u2014 combining strategy design, implementation, and transparent reporting in one place."
      },
      {
        q: "What is LetsTalkRelief.com?",
        a: "LetsTalkRelief.com is KGOB\u2019s secure platform for IRS issue resolution \u2014 back taxes, audits, and notices \u2014 combining automation, document tracking, and expert CPA guidance."
      },
      {
        q: "What is LetsTalkTaxes.io?",
        a: "LetsTalkTaxes.io is KGOB\u2019s free tax estimator that helps individuals and businesses calculate 2025 tax liability and understand the impact of new tax law changes."
      },
    ]
  },
  {
    id: 'working',
    title: 'Working With KGOB',
    icon: Users,
    questions: [
      {
        q: "How do I become a KGOB client?",
        a: "Start by reaching out through our Contact page at kgob.com/contact or call us at 1-844-599-3355. Our advisory team reviews your information, evaluates fit and capacity, and reaches out with next steps including a discovery call and proposal."
      },
      {
        q: "Why is KGOB selective about new clients?",
        a: "We protect service quality, turnaround times, and the experience our existing clients rely on. We accept a limited number of new relationships at any given time so every client gets the level of care they deserve."
      },
      {
        q: "Does completing the contact form guarantee KGOB will accept me?",
        a: "No \u2014 submitting the form does not create a CPA\u2013client relationship and does not guarantee acceptance. It\u2019s the first step in evaluating mutual fit. New inquiries may occasionally be placed on a waitlist when capacity is full."
      },
      {
        q: "What should I expect when working with KGOB?",
        a: "A calm, organized experience: clear onboarding, a reliable point of contact, year-round planning rather than once-a-year filing, clean books as a baseline, and timely responsive communication."
      },
      {
        q: "Does KGOB offer in-person meetings?",
        a: "Yes \u2014 in-person meetings are available when appropriate at our Charlotte, Winston-Salem, and Mooresville offices. The Charlotte Fairview location is by appointment."
      },
      {
        q: "Is KGOB a fit for solopreneurs or side hustles?",
        a: "We work best with businesses that have moved beyond the side-hustle stage and are serious about growth. If you\u2019re earlier in your journey, BizHub.com offers tools, education, and community to help you build a foundation."
      },
    ]
  },
  {
    id: 'tax',
    title: 'Tax & Financial Questions',
    icon: DollarSign,
    questions: [
      {
        q: "How often should I meet with my CPA?",
        a: "Most KGOB business clients meet quarterly, with year-end planning conversations and ad-hoc strategy calls before major decisions. Individual clients typically meet 1\u20133 times per year depending on complexity."
      },
      {
        q: "Will new tax law changes impact me in 2025\u20132026?",
        a: "Tax law changes regularly affect individuals and business owners. Our LetsTalkTaxes.io tool helps you see the specific impact on your situation, and our advisory team integrates new law into year-round planning."
      },
      {
        q: "How does KGOB protect my financial data?",
        a: "KGOB uses secure client portals, encrypted document handling, and platform-level security across all of our innovation tools (AskSara.cpa, LetsTalkRelief.com, etc.) to protect sensitive financial and tax information."
      },
      {
        q: "How do I get in touch with KGOB?",
        a: "Visit kgob.com/contact, call our toll-free number at 1-844-599-3355, or call our Charlotte HQ directly at (704) 599-3355. We respond within business hours."
      },
    ]
  },
];

function FAQItem({ question, answer, index }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.3, delay: index * 0.03 }}
      className="border-b border-[#0B2240]/10 last:border-0"
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-start justify-between py-5 text-left group"
        data-testid={`faq-q-${index}`}
      >
        <span className="font-semibold text-[#0B2240] pr-8 text-base leading-relaxed group-hover:text-[#D4AF37] transition-colors">
          {question}
        </span>
        <ChevronDown
          className={`w-5 h-5 text-[#D4AF37] shrink-0 mt-1 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          transition={{ duration: 0.2 }}
          className="pb-5"
        >
          <p className="text-[#4C5A70] leading-relaxed text-sm">{answer}</p>
        </motion.div>
      )}
    </motion.div>
  );
}

export default function FAQ() {
  // Build FAQPage schema for all 30 questions
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQ_SECTIONS.flatMap(section =>
      (section.questions || []).map(q => ({
        "@type": "Question",
        "name": q.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": q.a
        }
      }))
    )
  };

    useJsonLd('faq-page-0', faqSchema);

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      <Helmet>
        <title>Frequently Asked Questions | KGOB CPAs & Advisors</title>
        <meta name="description" content="Find answers about KGOB CPAs & Advisors — services, offices, technology platforms, how to become a client, tax planning, IRS help, and more. 30+ frequently asked questions answered." />
        <link rel="canonical" href="https://kgob.com/faq" />
        <meta property="og:title" content="FAQ | KGOB CPAs & Advisors" />
        <meta property="og:description" content="30+ answers about KGOB's services, technology, offices, and how we work. Tax, accounting, IRS help, and business advisory." />
        <meta property="og:url" content="https://kgob.com/faq" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://kgob.com/share/og/v1/kgob-share-default.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="FAQ | KGOB CPAs &amp; Advisors" />
        <meta name="twitter:description" content="30+ answers about KGOB's services, technology, offices, and how we work." />
        <meta name="twitter:image" content="https://kgob.com/share/og/v1/kgob-twitter-card.png" />
      </Helmet>

      <Header />

      <main>
        {/* Hero */}
        <section className="bg-[linear-gradient(180deg,#0B2240_0%,#07182F_100%)] text-white py-16 lg:py-20">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <p className="uppercase tracking-[0.18em] text-xs text-[#E3C96E] mb-3">ANSWERS TO YOUR QUESTIONS</p>
              <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-semibold leading-tight">
                Frequently Asked Questions
              </h1>
              <p className="mt-4 text-lg text-white/80 max-w-2xl mx-auto">
                Everything you need to know about KGOB CPAs & Advisors, our services, technology platforms, and how we work.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Quick Nav */}
        <section className="border-b border-[#0B2240]/10 bg-white sticky top-0 z-10">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <div className="flex gap-1 overflow-x-auto py-3" data-testid="faq-nav">
              {FAQ_SECTIONS.map(section => (
                <a data-testid="f-a-q-a-1"
                  key={section.id}
                  href={`#${section.id}`}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium text-[#4C5A70] hover:text-[#0B2240] hover:bg-[#0B2240]/5 whitespace-nowrap transition-colors"
                >
                  <section.icon className="w-4 h-4" />
                  {section.title}
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Sections */}
        <section className="py-12 lg:py-16">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 space-y-12">
            {FAQ_SECTIONS.map((section, sIdx) => (
              <div key={section.id} id={section.id}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4 }}
                  className="flex items-center gap-3 mb-6"
                >
                  <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                    <section.icon className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0B2240]">
                    {section.title}
                  </h2>
                </motion.div>

                <div className="bg-white rounded-2xl shadow-[0_4px_20px_rgba(11,34,64,0.06)] p-6 sm:p-8">
                  {(section.questions || []).map((item, qIdx) => (
                    <FAQItem
                      key={qIdx}
                      question={item.q}
                      answer={item.a}
                      index={sIdx * 10 + qIdx}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 lg:py-16 bg-[#0B2240]">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-semibold text-white mb-4">
              Still Have Questions?
            </h2>
            <p className="text-white/70 mb-8 max-w-xl mx-auto">
              Our team is ready to help. Reach out through any of these channels.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="/contact"
                className="inline-flex items-center px-8 py-3 bg-[#D4AF37] text-[#0B2240] font-semibold rounded-full hover:brightness-110 transition-all"
                data-testid="faq-cta-contact"
              >
                Contact Us
              </a>
              <a
                href="tel:+18445993355"
                className="inline-flex items-center px-8 py-3 border border-white/30 text-white font-medium rounded-full hover:bg-white/10 transition-all"
                data-testid="faq-cta-call"
              >
                Call 1-844-599-3355
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
